import { useEffect, useMemo, useState } from 'react';
import { latLngBounds, type LatLngTuple } from 'leaflet';
import { CircleMarker, Marker, Polyline, Tooltip, useMap } from 'react-leaflet';
import { MapPinOff, Maximize2, XIcon } from 'lucide-react';
import { BangladeshMap } from '@/components/map/BangladeshMap';
import { MapAlertPill } from '@/components/map/MapAlertPill';
import { MapStyleToggle } from '@/components/map/MapStyleToggle';
import { MAP_CLOSE_UP_ZOOM, MAP_LARGE_LABEL_CLOSE_UP_ZOOM } from '@/components/map/map.constants';
import { Button } from '@/components/ui/button';
import { Dialog, DialogClose, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import type {
  InstituteSubmissionDetail,
  SubmissionStatus,
} from '../types/instituteSubmission.types';
import { getMapPosition } from '../utils/mapPosition';
import { getSubmissionPinIcon } from '../utils/submissionPinIcon';

const FIT_PADDING: [number, number] = [64, 64];
const STORED_LOCATION_RADIUS_PX = 7;
const STORED_LOCATION_RING_WEIGHT = 3;
const DISTANCE_LINE_WEIGHT = 2;
const DISTANCE_LINE_DASH = '6 6';
const MAP_DIALOG_TITLE_FALLBACK = 'Institute location';
const VIEW_MAP_BUTTON_CLASS = cn(
  'absolute bottom-3 left-1/2 z-10 inline-flex -translate-x-1/2 cursor-zoom-in items-center gap-1.5 rounded-md bg-black/60 px-3 py-1.5 text-xs font-medium text-white transition-opacity hover:bg-black/75',
  'pointer-events-none opacity-0 group-hover:pointer-events-auto group-hover:opacity-100',
  'focus-visible:pointer-events-auto focus-visible:opacity-100 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none',
  'pointer-coarse:pointer-events-auto pointer-coarse:min-h-10 pointer-coarse:opacity-100',
);

const isPosition = (position: LatLngTuple | null): position is LatLngTuple => position !== null;

interface SubmissionMapPositions {
  capturePosition: LatLngTuple | null;
  storedPosition: LatLngTuple | null;
  positions: LatLngTuple[];
}

interface FitToPositionsProps {
  positions: LatLngTuple[];
  maxZoom: number;
}

function FitToPositions({ positions, maxZoom }: FitToPositionsProps) {
  const map = useMap();

  useEffect(() => {
    if (positions.length === 0) return;
    map.fitBounds(latLngBounds(positions), {
      padding: FIT_PADDING,
      maxZoom,
      animate: false,
    });
  }, [map, positions, maxZoom]);

  return null;
}

interface LocationMapCanvasProps {
  status: SubmissionStatus;
  mapPositions: SubmissionMapPositions;
  isSatellite: boolean;
  onSatelliteChange: (isSatellite: boolean) => void;
  hasLargeLabels?: boolean;
}

function LocationMapCanvas({
  status,
  mapPositions,
  isSatellite,
  onSatelliteChange,
  hasLargeLabels = false,
}: LocationMapCanvasProps) {
  const { capturePosition, storedPosition, positions } = mapPositions;
  const fitMaxZoom = hasLargeLabels ? MAP_LARGE_LABEL_CLOSE_UP_ZOOM : MAP_CLOSE_UP_ZOOM;

  return (
    <div className="relative size-full">
      <BangladeshMap isSatellite={isSatellite} hasLargeLabels={hasLargeLabels}>
        <FitToPositions positions={positions} maxZoom={fitMaxZoom} />
        {capturePosition && storedPosition && (
          <Polyline
            positions={[storedPosition, capturePosition]}
            interactive={false}
            className="stroke-foreground/60"
            pathOptions={{ weight: DISTANCE_LINE_WEIGHT, dashArray: DISTANCE_LINE_DASH }}
          />
        )}
        {storedPosition && (
          <CircleMarker
            center={storedPosition}
            radius={STORED_LOCATION_RADIUS_PX}
            className="fill-background stroke-foreground"
            pathOptions={{ weight: STORED_LOCATION_RING_WEIGHT, fillOpacity: 1 }}
          >
            <Tooltip direction="top">Location on file</Tooltip>
          </CircleMarker>
        )}
        {capturePosition && (
          <Marker position={capturePosition} icon={getSubmissionPinIcon(status)}>
            <Tooltip direction="top">Photo GPS point</Tooltip>
          </Marker>
        )}
      </BangladeshMap>

      <MapStyleToggle
        isSatellite={isSatellite}
        onChange={onSatelliteChange}
        showsOnlyOtherStyle
        className="absolute top-2 right-2 z-10"
      />

      {!capturePosition && (
        <MapAlertPill icon={MapPinOff} message="No GPS point" className="inset-x-4 bottom-12" />
      )}
    </div>
  );
}

interface InstituteSubmissionLocationMapProps {
  submission: InstituteSubmissionDetail;
}

export function InstituteSubmissionLocationMap({
  submission,
}: InstituteSubmissionLocationMapProps) {
  const [isSatellite, setIsSatellite] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const mapPositions = useMemo<SubmissionMapPositions>(() => {
    const photoPosition = getMapPosition(submission);
    const fileLocationPosition = getMapPosition({
      latitude: submission.storedLatitude,
      longitude: submission.storedLongitude,
    });
    return {
      capturePosition: photoPosition,
      storedPosition: fileLocationPosition,
      positions: [photoPosition, fileLocationPosition].filter(isPosition),
    };
  }, [submission]);

  return (
    <>
      <div className="group relative size-full">
        <LocationMapCanvas
          status={submission.status}
          mapPositions={mapPositions}
          isSatellite={isSatellite}
          onSatelliteChange={setIsSatellite}
        />
        <button type="button" onClick={() => setIsExpanded(true)} className={VIEW_MAP_BUTTON_CLASS}>
          <Maximize2 className="size-4" aria-hidden />
          View map
        </button>
      </div>

      <Dialog open={isExpanded} onOpenChange={setIsExpanded}>
        <DialogContent
          showCloseButton={false}
          className="flex h-[90dvh] flex-col gap-0 overflow-hidden p-0 sm:max-w-6xl"
        >
          <div className="flex shrink-0 items-center justify-between gap-2 border-b py-2 pr-2 pl-4">
            <DialogTitle className="truncate text-sm font-medium">
              {submission.current.name ?? MAP_DIALOG_TITLE_FALLBACK}
            </DialogTitle>
            <DialogClose render={<Button variant="ghost" size="icon-sm" />}>
              <XIcon />
              <span className="sr-only">Close</span>
            </DialogClose>
          </div>
          <div className="min-h-0 flex-1">
            <LocationMapCanvas
              status={submission.status}
              mapPositions={mapPositions}
              isSatellite={isSatellite}
              onSatelliteChange={setIsSatellite}
              hasLargeLabels
            />
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
